import type {
  ApiBookingUpdate,
  BookingFlags,
  ContainerLine,
  OperationalSnapshot,
} from "@/types/api";
import type { DisplayPricingMeta } from "@/types/pricingTier";
import type { RebookInfo } from "@/types/rebook";

export type { BookingFlags, BookingTagMeta } from "@/types/api";

export type BookingState =
  | "pending"
  | "pending_book"
  | "book"
  | "book_restricted"
  | "rolled"
  | "stuffed"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "rebooked";

export interface PortInfo {
  locode: string;
  name: string;
}

export interface MockBooking {
  id: string;
  source_id: string;
  /** Focus "File"/dossier number ("מס' תיק"), when linked. Distinct from the
   * order number (source_id); shown next to it as a secondary reference. */
  file_id?: string;
  state: BookingState;
  tags: string[];
  /** Enriched tag metadata keyed by contract_ref (or `""` for booking-level). */
  flags?: BookingFlags;
  carrier?: string;
  contract_ref?: string;
  /** Named account from the matched rate card, when different from the ref. */
  named_account?: string;
  named_accounts?: Record<string, string>;
  all_contract_refs?: string;
  all_carriers?: string;
  /** Rate card the current contract resolves to, when pricing lines are loaded. */
  rate_card_id?: string;
  /** Carrier booking confirmation from the agent update, when present. */
  booking_ref?: string;
  origin_port: PortInfo;
  destination_port: PortInfo;
  departure_date?: string;
  arrival_date?: string;
  stuffing_date?: string;
  /** Provenance of stuffing_date: "update" | "carrier_tracking". */
  stuffing_date_source?: string;
  /** Cargo ready date from the agent update / ERP, when set. */
  ready_date?: string;
  cutoff_date?: string;
  /** Agent cutoff if present, otherwise the schedule-derived cargo/CY cutoff. */
  effective_cutoff?: string;
  /** Provenance of effective_cutoff: "update" | "schedule". */
  cutoff_source?: string;
  effective_pcd?: string;
  /** True when effective_pcd is the ETD because no agent PCD was provided. */
  pcd_is_etd_fallback?: boolean;
  /** Provenance of the resolved PCD: "agent" | "carrier_invoice". */
  pcd_source?: string;
  pricing_date?: string;
  /** True when pricing_date is frozen to an earlier date than the current ETD. */
  pricing_date_is_frozen?: boolean;
  state_changed_at?: string;
  vessel?: string;
  equipment_summary: string;
  total_teu?: number;
  total_containers?: number;
  load_type?: string;
  /** Agent-level cargo weight fallback in kg when lines have no weight. */
  weight?: number;
  containers?: ContainerLine[];
  source_containers?: ContainerLine[];
  update_source?: string;
  updates?: ApiBookingUpdate[];
  shipper?: string;
  shipper_name?: string;
  consignee?: string;
  consignee_name?: string;
  agent?: string;
  /** Raw Focus "Foreign/Remote Agent" text (handling agent assigned by Focus). */
  focus_agent_raw?: string;
  /** Source of the current agent-feed update, null when no feed is active. */
  active_agent_update_source?: string;
  week?: string;
  /** System-wide service-trade ("main line") grouping from effective ports. */
  service_trade?: string;
  commodity?: string;
  open_date?: string;
  /** Most recent booking-update snapshot (last updated). */
  snapshot_date?: string;
  sales_person?: string;
  opened_by?: string;
  pricing_total?: number;
  pricing_currency?: string;
  /** True when every container line has a rate match. Omitted/false → no price. */
  pricing_matched?: boolean;
  best_saving?: number;
  first_book_price?: number;
  first_book_rec_id?: string;
  first_book_carrier?: string;
  first_book_contract?: string;
  /** Chain-net rebook info: linked booking + realized before/after pricing. */
  rebook_info?: RebookInfo | null;
  bl_group_id?: string;
  /** Per-booking BL charge after splitting across BL group members. */
  bl_charge?: number;
  operational?: OperationalSnapshot | null;
  /** Set when a carrier-tracking snapshot exists for this booking. */
  tracking_fetched_at?: string;
  /**
   * Canonical carrier Bill of Lading number (doc-intel B/L when available, else
   * carrier tracking). Single source of truth for carrier-invoice linking.
   */
  carrier_bl_number?: string;
  /** Provenance of `carrier_bl_number`: "doc_intel" | "carrier_tracking". */
  carrier_bl_source?: string;
  /** Pricing tier resolution metadata (present when a non-full tier applied). */
  display_pricing?: DisplayPricingMeta;
  /**
   * Consignee (Client) invoice/quote total — the only price a `client_invoice`
   * tier viewer sees. Present only when wired through; never internal cost.
   */
  consignee_invoice_total?: number;
  consignee_invoice_currency?: string;
  /** Booking JSON extras — includes PDF `party_contacts` when ingested. */
  extra_data?: Record<string, unknown> | null;
}

export const BOOKING_STATE_LABELS: Record<BookingState, string> = {
  pending: "Pending",
  pending_book: "Pending book",
  book: "Booked",
  book_restricted: "Restricted",
  rolled: "Rolled",
  stuffed: "Stuffed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  rebooked: "Rebooked",
};

export type BookingStateFilter = BookingState | "all" | "active";

/**
 * State pills in the bookings header filter (multi-select).
 *
 * The Rolled pill sends `state=rolled`. The API also matches the `ROLLED` tag
 * so currently-rolled bookings and historically rolled shipped/delivered rows
 * appear together.
 */
export const BOOKING_STATE_OPTIONS: {
  value: BookingStateFilter;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "book", label: "Booked" },
  { value: "rolled", label: "Rolled" },
  { value: "stuffed", label: "Stuffed" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
];

/** States considered "active" (in-flight) for the Active filter pill. */
const ACTIVE_STATES: BookingState[] = [
  "book",
  "book_restricted",
  "stuffed",
  "shipped",
];

/** States treated as "pending" (Pending + Pending Book). */
export const PENDING_STATES: BookingState[] = ["pending", "pending_book"];

/**
 * Expand a `state` API/filter value so UI "pending" also matches Pending Book.
 * An explicit `pending_book` (or any other state) is left unchanged.
 */
export function expandPendingStateParam(
  state: string | undefined,
): string | undefined {
  if (!state) return state;
  const values = state
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  if (values.includes("pending") && !values.includes("pending_book")) {
    const pendingIdx = values.indexOf("pending");
    values.splice(pendingIdx + 1, 0, "pending_book");
  }
  return values.join(",");
}

function statesFromFilterKeys(
  keys: readonly BookingStateFilter[],
): string | undefined {
  const states = new Set<string>();
  for (const key of keys) {
    if (key === "all") continue;
    if (key === "active") {
      ACTIVE_STATES.forEach((state) => states.add(state));
    } else if (key === "pending") {
      PENDING_STATES.forEach((state) => states.add(state));
    } else {
      states.add(key);
    }
  }
  if (states.size === 0) return undefined;
  return expandPendingStateParam([...states].join(","));
}

/**
 * Translate UI state filter selection into the API `state` query value
 * (comma-separated list, or undefined for "all").
 *
 * `rolled` is sent as-is; the bookings list expands it server-side to also
 * match the `ROLLED` tag (historical rolls after ship/deliver).
 */
export function stateFilterToApiParam(
  filter: BookingStateFilter | readonly BookingStateFilter[],
): string | undefined {
  // `Array.isArray` widens to `any[]` and doesn't narrow a `readonly` union, so
  // normalise to an array explicitly before delegating.
  const keys: readonly BookingStateFilter[] = Array.isArray(filter)
    ? (filter as readonly BookingStateFilter[])
    : [filter as BookingStateFilter];
  if (keys.length === 0 || keys.includes("all")) return undefined;
  return statesFromFilterKeys(keys);
}

/** True when a booking state matches any selected header filter pill. */
export function matchesStateFilterSelection(
  state: BookingState,
  selection: readonly BookingStateFilter[],
): boolean {
  if (selection.length === 0 || selection.includes("all")) return true;
  return selection.some((key) => matchesStateFilter(state, key));
}

export function formatBookingDate(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatUsd(amount?: number): string {
  if (amount == null) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Booking all-in price to display. A stored $0 is treated as missing so
 * tables and details show a dash. Agent-priced (unmatched) positives are
 * kept — they are the assigned rate, same as frontend v1.
 */
export function matchedBookingPrice(
  amount?: number | null,
  _matched?: boolean | null,
): number | undefined {
  if (amount == null || !Number.isFinite(amount) || amount === 0) {
    return undefined;
  }
  return amount;
}

export function routeLabel(booking: Pick<MockBooking, "origin_port" | "destination_port">): string {
  return `${booking.origin_port.locode} → ${booking.destination_port.locode}`;
}

export function matchesStateFilter(
  state: BookingState,
  filter: BookingStateFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "active") return ACTIVE_STATES.includes(state);
  if (filter === "pending") return PENDING_STATES.includes(state);
  return state === filter;
}
